// PlpResultConstants, getOrDefaultValue and isMobile are local stubs until the lib utilities are migrated.
const PlpResultConstants: Record<string, string> = {};
const getOrDefaultValue = (val: string | undefined, defaultVal: string): string =>
  val ?? defaultVal;
const isMobile = (): boolean => false;
// IAuthData, IFilterKeys, IFiltersData, IRequestParams and IResultRequestParams are local stubs
// until the TypePlpFilters component types are migrated to this package.
type IFilterItem = { key: string; displayLabel?: string };
type IAuthData = Record<string, IFilterItem>;
type IFiltersData = Record<string, unknown[]>;
type IRequestParams = { filters?: Record<string, unknown> };
type IResultRequestParams = Record<string, unknown>;

export const generateFilterTitle = (key: string, filterObject: IAuthData): string => {
  const filterItem = Object.values(filterObject).find((item) => item?.key === key);
  return filterItem?.displayLabel ?? "";
};

export const generateFilterTabList = (requestParams: IResultRequestParams): string[] => {
  const activeFilterLabels: unknown[] = [];
  const tileLabel = extractTileAmtLabelQueryParam();
  if (tileLabel) {
    activeFilterLabels.push(tileLabel);
  }
  for (const [key, value] of Object.entries(requestParams)) {
    if (!keysToExclude().includes(key)) {
      const arr = value as unknown[];
      if (arr?.length) {
        activeFilterLabels.push(value);
      }
    }
  }
  return activeFilterLabels.flat() as string[];
};

export const isSelectAllChecked = (
  filterKey: string,
  filters: IFiltersData,
  requestParams: IResultRequestParams
): boolean => filters[filterKey]?.length === (requestParams[filterKey] as unknown[])?.length;

export const setRequestParamsToQuery = (
  requestParams: IResultRequestParams,
  redirectionUrl?: string | null
): void => {
  const url = new URL(globalThis.window.location.href);
  const queryParams = url.searchParams;
  queryParams.delete("token");
  queryParams.delete("key");
  queryParams.delete("src");
  for (const [key, value] of Object.entries(requestParams)) {
    if (!keysToExclude().includes(key)) {
      queryParams.set(key, JSON.stringify(value));
    }
  }
  if (redirectionUrl) {
    globalThis.window.location.href = `${redirectionUrl}?${queryParams.toString()}`;
  } else {
    globalThis.window.history.pushState({}, "", `${url.pathname}?${queryParams.toString()}`);
  }
};

export const getRequestParamsFromUrl = (
  requestParams: IResultRequestParams
): IResultRequestParams => {
  const url = new URL(globalThis.window.location.href);
  const queryParams = url.searchParams;
  for (const [key, value] of queryParams) {
    if (key in requestParams && !keysToExclude().includes(key)) {
      requestParams[key] = JSON.parse(value) as unknown;
    }
  }
  for (const key of Object.keys(requestParams)) {
    if (!queryParams.get(key) && !keysToExclude().includes(key) && key !== "sortBy") {
      requestParams[key] = [];
    }
  }
  return requestParams;
};

export const removeAllQueryParams = (
  requestParams: IResultRequestParams,
  redirectionUrl?: string | null
): void => {
  const url = new URL(globalThis.window.location.href);
  const queryParams = url.searchParams;
  for (const [key, value] of Object.entries(requestParams)) {
    if (!keysToExclude().includes(key)) {
      queryParams.delete(key, JSON.stringify(value));
    }
  }

  if (redirectionUrl) {
    globalThis.window.location.href = `${redirectionUrl}`;
  } else {
    globalThis.window.history.pushState({}, "", `${url.pathname}`);
  }
};

export const initializeRequestBody = (
  requestData: IResultRequestParams,
  filters: IFiltersData
): IResultRequestParams => {
  const requestBody: IResultRequestParams = { ...requestData };
  for (const key of Object.keys(filters)) {
    requestBody[key] = [];
  }
  return requestBody;
};

export const getFilterCount = (reqBody: IResultRequestParams): number => {
  let count = 0;
  for (const [key, value] of Object.entries(reqBody)) {
    if (Array.isArray(value)) {
      count += value.length;
    } else if (!keysToExclude().includes(key) && (value as string).length) {
      count += 1;
    }
  }
  return count;
};

export const getDynamicFilterCount = (reqBody: IRequestParams): number => {
  return reqBody.filters ? Object.keys(reqBody.filters).length : 0;
};

export const extractTileAmtQueryParam = (): unknown => {
  try {
    const searchParams = new URLSearchParams(globalThis.window.location.href.split("?")[1]);
    const tileAmountValueEncoded = searchParams.get("tileAmountValue");
    return tileAmountValueEncoded ? JSON.parse(decodeURIComponent(tileAmountValueEncoded)) : null;
  } catch (error) {
    console.error("Error parsing URL:", error);
    return null;
  }
};

export const extractTileAmtLabelQueryParam = (): unknown => {
  try {
    const searchParams = new URLSearchParams(globalThis.window.location.href.split("?")[1]);
    const tileAmountLabelEncoded = searchParams.get("tileAmountLabel");
    return tileAmountLabelEncoded ? JSON.parse(decodeURIComponent(tileAmountLabelEncoded)) : null;
  } catch (error) {
    console.error("Error parsing URL:", error);
    return null;
  }
};

export const keysToExclude = (): string[] => [
  "category",
  "subCategory",
  "resultsLimit",
  "offset",
  "includeIcorsProducts",
  "locale",
];

export const getResultLimit = (
  componentType: string,
  resultsLimitMobile: string | undefined,
  resultsLimit: number
): number =>
  componentType === PlpResultConstants.CLP_PAGE && isMobile()
    ? +getOrDefaultValue(resultsLimitMobile, resultsLimit.toString())
    : +resultsLimit;
